/**
 * 0.45.61 - Keeps an API-paged Table or Repeating display and its
 * paginator in step with the API (lib/apiPaging.js).
 *
 *   - Reports the number of matches to the paginator: the answer's total
 *     (api_paging_total_path), or, with none, enough for one more page
 *     while a page comes back full.
 *   - When an answer arrives, the paginator shows the page it was for
 *     (a new search starts at page 1).
 *   - When the visitor picks another page or page size, asks the API for
 *     it, with the same inputs as the last call (the search) and the new
 *     page's.
 *
 * An API table WITHOUT API paging (the whole list in one answer, split
 * by the paginator here) gets its row count reported too: a table's
 * count used to come only from its authored rows, so an API table's
 * paginator stayed hidden and rows past the first page could not be
 * reached.
 *
 * Renders nothing.
 */
import { useContext, useEffect, useRef } from 'react';
import { PaginationContext } from './PaginationContext';
import { readPath } from '../lib/apiResponsePath';
import { apiPagingOf, inputsForPage, pageOfInputs } from '../lib/apiPaging';

export default function ApiPageDriver({ el, paginationKey, ctx, rowsOnPage = 0 }) {
  const pctx = useContext(PaginationContext);
  const pg = apiPagingOf(el);
  const slug = (el && el.api_connection ? String(el.api_connection) : '').trim();
  const entry = (ctx && ctx.apiState && slug) ? ctx.apiState[slug] : null;
  const pstate = (pctx && pctx.states && paginationKey) ? pctx.states[paginationKey] : null;
  const lastResponse = useRef(undefined);

  // The number of matches, for the paginator's page count.
  const cur = pg && entry ? pageOfInputs(pg, entry.inputs) : null;
  let total = 0;
  const clientPaged = !pg && !!(el && el.source === 'api' && el.pagination_enabled);
  if (clientPaged) total = rowsOnPage;
  if (pg && entry && entry.response !== undefined) {
    // Only a number counts: a path the answer lacks reads as null,
    // which Number() would turn into 0 and hide the paginator.
    const raw = pg.totalPath ? readPath(entry.response, pg.totalPath) : null;
    const t = (typeof raw === 'number' || (typeof raw === 'string' && raw.trim() !== ''))
      ? Number(raw) : NaN;
    if (Number.isFinite(t) && t >= 0) total = t;
    else if (cur) {
      total = (cur.page - 1) * cur.per + rowsOnPage + (rowsOnPage >= cur.per ? 1 : 0);
    }
  }
  useEffect(() => {
    if ((!pg && !clientPaged) || !pctx || !pctx.reportRowCount || !paginationKey) return;
    pctx.reportRowCount(paginationKey, total);
  }, [pg ? 1 : 0, clientPaged, pctx, paginationKey, total]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pg || !entry || !pctx || !paginationKey) return;
    if (entry.status === 'pending' || entry.response === undefined || !cur) return;
    if (entry.response !== lastResponse.current) {
      // A new answer: the paginator follows it.
      lastResponse.current = entry.response;
      if (!pstate || pstate.currentPage !== cur.page || pstate.perPage !== cur.per) {
        pctx.update(paginationKey, { currentPage: cur.page, perPage: cur.per });
      }
      return;
    }
    // Same answer, another page or page size picked: ask for it.
    if (pstate && (pstate.currentPage !== cur.page || pstate.perPage !== cur.per)
        && ctx && typeof ctx.callApiPage === 'function') {
      const page = pstate.perPage !== cur.per ? 1 : pstate.currentPage;
      ctx.callApiPage(slug, { ...(entry.inputs || {}), ...inputsForPage(pg, page, pstate.perPage) });
    }
  });

  return null;
}
